import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleLabels } from "@/lib/constants";
import { OfflineBanner } from "@/components/OfflineBanner";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, BookOpen, ClipboardList, GraduationCap, QrCode, FileText,
  Users, Building2, CalendarDays, Megaphone, Bell, LogOut, Menu, X, Award, UserCheck, BarChart3, Star, ShieldCheck, Bot, Trophy, Landmark, MessageCircle, CheckCheck, KeyRound, Flag, Share2, Images, Activity, CalendarCheck, ScrollText, Contact
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navByRole = {
  admin: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/programs", label: "البرامج العلمية", icon: BookOpen },
    { to: "/app/enrollments", label: "التسجيلات", icon: UserCheck },
    { to: "/app/lectures", label: "المحاضرات", icon: GraduationCap },
    { to: "/app/attendance", label: "الحضور (QR)", icon: QrCode },
    { to: "/app/assignments", label: "الواجبات", icon: ClipboardList },
    { to: "/app/teachers", label: "المدرسون", icon: Users },
    { to: "/app/units", label: "الوحدات السكنية", icon: Building2 },
    { to: "/app/calendar", label: "التقويم العلمي", icon: CalendarDays },
    { to: "/app/announcements", label: "الإعلانات", icon: Megaphone },
    { to: "/app/evaluations", label: "التقييمات", icon: Star },
    { to: "/app/reports", label: "التقارير", icon: BarChart3 },
    { to: "/app/assistant", label: "المساعد الذكي", icon: Bot },
    { to: "/app/mosques", label: "المساجد", icon: Landmark },
    { to: "/app/consultants", label: "دليل المستشارين", icon: Contact },
    { to: "/app/chat", label: "المحادثات", icon: MessageCircle },
    { to: "/app/complaints", label: "الشكاوى", icon: Flag },
    { to: "/app/social", label: "مواقع التواصل", icon: Share2 },
    { to: "/app/album", label: "ألبوم المنصة", icon: Images },
    { to: "/app/activities", label: "الأنشطة", icon: Activity },
    { to: "/app/attendance-logs", label: "سجلات الحضور", icon: ClipboardList },
    { to: "/app/certificates", label: "الشهادات", icon: ScrollText },
    { to: "/app/supervisor-requests", label: "طلبات الإشراف", icon: ShieldCheck },
  ],
  supervisor: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/programs", label: "البرامج العلمية", icon: BookOpen },
    { to: "/app/enrollments", label: "التسجيلات", icon: UserCheck },
    { to: "/app/lectures", label: "المحاضرات", icon: GraduationCap },
    { to: "/app/attendance", label: "الحضور (QR)", icon: QrCode },
    { to: "/app/assignments", label: "الواجبات", icon: ClipboardList },
    { to: "/app/teachers", label: "المدرسون", icon: Users },
    { to: "/app/units", label: "الوحدات السكنية", icon: Building2 },
    { to: "/app/calendar", label: "التقويم العلمي", icon: CalendarDays },
    { to: "/app/announcements", label: "الإعلانات", icon: Megaphone },
    { to: "/app/evaluations", label: "التقييمات", icon: Star },
    { to: "/app/reports", label: "التقارير", icon: BarChart3 },
    { to: "/app/assistant", label: "المساعد الذكي", icon: Bot },
    { to: "/app/mosques", label: "المساجد", icon: Landmark },
    { to: "/app/consultants", label: "دليل المستشارين", icon: Contact },
    { to: "/app/chat", label: "المحادثات", icon: MessageCircle },
    { to: "/app/complaints", label: "الشكاوى", icon: Flag },
    { to: "/app/social", label: "مواقع التواصل", icon: Share2 },
    { to: "/app/activities", label: "الأنشطة", icon: Activity },
    { to: "/app/attendance-logs", label: "سجلات الحضور", icon: ClipboardList },
    { to: "/app/certificates", label: "الشهادات", icon: ScrollText },
  ],
  teacher: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/programs", label: "برامجي", icon: BookOpen },
    { to: "/app/lectures", label: "المحاضرات", icon: GraduationCap },
    { to: "/app/attendance", label: "الحضور (QR)", icon: QrCode },
    { to: "/app/assignments", label: "الواجبات", icon: ClipboardList },
    { to: "/app/calendar", label: "التقويم العلمي", icon: CalendarDays },
    { to: "/app/announcements", label: "الإعلانات", icon: Megaphone },
    { to: "/app/evaluations", label: "نتائج التقييم", icon: Star },
    { to: "/app/reports", label: "التقارير", icon: BarChart3 },
    { to: "/app/mosques", label: "المساجد", icon: Landmark },
    { to: "/app/consultants", label: "دليل المستشارين", icon: Contact },
    { to: "/app/chat", label: "المحادثات", icon: MessageCircle },
    { to: "/app/social", label: "مواقع التواصل", icon: Share2 },
    { to: "/app/activities", label: "الأنشطة", icon: Activity },
  ],
  student: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/programs", label: "البرامج المتاحة", icon: BookOpen },
    { to: "/app/enrollments", label: "برامجي", icon: UserCheck },
    { to: "/app/attendance", label: "تسجيل الحضور", icon: QrCode },
    { to: "/app/assignments", label: "الواجبات", icon: ClipboardList },
    { to: "/app/grades", label: "درجاتي", icon: Award },
    { to: "/app/certificates", label: "شهاداتي", icon: ScrollText },
    { to: "/app/leaderboard", label: "لوحة الترتيب", icon: Trophy },
    { to: "/app/calendar", label: "التقويم العلمي", icon: CalendarDays },
    { to: "/app/announcements", label: "الإعلانات", icon: Megaphone },
    { to: "/app/evaluations", label: "تقييم البرامج", icon: Star },
    { to: "/app/mosques", label: "المساجد", icon: Landmark },
    { to: "/app/consultants", label: "دليل المستشارين", icon: Contact },
    { to: "/app/chat", label: "المحادثات", icon: MessageCircle },
    { to: "/app/complaints", label: "الشكاوى", icon: Flag },
    { to: "/app/social", label: "مواقع التواصل", icon: Share2 },
    { to: "/app/activities", label: "الأنشطة", icon: Activity },
    { to: "/app/my-activities", label: "أنشطتي", icon: CalendarCheck },
  ],
};

function Logo() {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-gold">
        <GraduationCap className="h-6 w-6" />
      </div>
      <div className="leading-tight">
        <p className="font-heading text-sm font-extrabold text-navy">سُفراء</p>
      </div>
    </div>
  );
}

function NotificationItem({ note }) {
  const [done, setDone] = useState(false);
  const lectureId = note.data?.lecture_id;
  const rsvp = async () => {
    try { await api.post(`/lectures/${lectureId}/rsvp`); setDone(true); toast.success("تم تأكيد نيتك للحضور"); }
    catch (e) { toast.error("تعذّر التأكيد"); }
  };
  return (
    <div className="border-b border-slate-50 p-3 hover:bg-slate-50">
      <p className="text-sm font-semibold text-navy">{note.title}</p>
      <p className="whitespace-pre-line text-xs text-slate-500">{note.body}</p>
      {note.kind === "lecture_reminder" && lectureId && (
        done
          ? <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCheck className="h-3.5 w-3.5" /> أكّدت حضورك</span>
          : <button data-testid={`rsvp-note-${lectureId}`} onClick={rsvp} className="mt-2 rounded-lg bg-greenx px-3 py-1 text-xs font-bold text-white hover:bg-greenx-light">سأحضر</button>
      )}
    </div>
  );
}

function NotificationsBell() {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = notes.filter((n) => !n.read).length;

  const load = () => api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const markRead = async () => { await api.put("/notifications/read"); load(); };

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o && unread) markRead(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-btn" className="relative rounded-xl border border-slate-200 bg-white p-2.5 text-slate-600 transition-colors hover:bg-slate-50">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-1 -left-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-navy">{unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notifications-menu">
        <div className="border-b border-slate-100 p-3 font-heading font-bold text-navy">التنبيهات</div>
        <div className="max-h-96 overflow-y-auto">
          {notes.length === 0 && <p className="p-6 text-center text-sm text-slate-400">لا توجد تنبيهات</p>}
          {notes.map((n) => <NotificationItem key={n.id} note={n} />)}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ChangePasswordButton() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (nw.length < 6) { toast.error("كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل"); return; }
    if (nw !== cf) { toast.error("تأكيد كلمة المرور غير مطابق"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("تم تغيير كلمة المرور بنجاح");
      setOpen(false); setCur(""); setNw(""); setCf("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <>
      <button data-testid="change-password-btn" onClick={() => setOpen(true)} title="تغيير كلمة المرور"
        className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-600 transition-colors hover:bg-slate-50">
        <KeyRound className="h-5 w-5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-navy">تغيير كلمة المرور</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>كلمة المرور الحالية</Label><Input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5 rounded-lg" /></div>
            <div><Label>كلمة المرور الجديدة</Label><Input data-testid="cp-new" type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="mt-1.5 rounded-lg" /></div>
            <div><Label>تأكيد كلمة المرور الجديدة</Label><Input data-testid="cp-confirm" type="password" value={cf} onChange={(e) => setCf(e.target.value)} className="mt-1.5 rounded-lg" /></div>
          </div>
          <DialogFooter><Button data-testid="cp-save" onClick={submit} disabled={saving || !cur || !nw} className="rounded-xl bg-navy hover:bg-navy-light">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const items = navByRole[user?.role] || [];

  useEffect(() => {
    const load = () => api.get("/chat/unread-count").then((r) => setChatUnread(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - right in RTL */}
      <aside className={`fixed inset-y-0 right-0 z-40 flex w-72 transform flex-col border-l border-slate-100 bg-white transition-transform duration-300 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"}`}>
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 p-5">
          <Logo />
          <button className="lg:hidden" onClick={() => setMobileOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <nav className="sidebar-scroll flex flex-1 flex-col gap-1 overflow-y-auto p-4">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.to.split("/").pop() || "home"}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${
                  isActive ? "bg-navy text-white shadow-sm" : "text-slate-600 hover:-translate-x-1 hover:bg-slate-50"
                }`
              }
            >
              <it.icon className="h-5 w-5" />
              <span className="flex-1">{it.label}</span>
              {it.to === "/app/chat" && chatUnread > 0 && (
                <span data-testid="nav-chat-badge" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-navy">{chatUnread}</span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="lg:mr-72">
        <OfflineBanner />
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200/60 bg-white/80 px-5 py-3 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-6 w-6 text-navy" /></button>
            <div>
              <p className="font-heading text-sm font-bold text-navy">مرحبًا، {user?.name}</p>
              <p className="text-xs text-slate-400">{roleLabels[user?.role]}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ChangePasswordButton />
            <NotificationsBell />
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-navy text-sm font-bold text-gold">
              {user?.name?.charAt(0)}
            </div>
            <button data-testid="logout-btn" onClick={doLogout} className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-600 transition-colors hover:bg-red-50 hover:text-red-500">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>
        <main className="mx-auto max-w-7xl p-5 sm:p-8">{children}</main>
      </div>
    </div>
  );
}
